import { Button } from '@elements/button'
import { Modal, ModalProps } from '@elements/modal'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { useOptimizedImage } from '@hooks/optimized-image'
import { useCallback } from 'react'

export interface ImageModalProps extends ModalProps {
  sources: string[]
  selectedIndex?: number
  setSelectedIndex?: (idx?: number) => void
  alt?: string
}

export function ImageModal({
  sources,
  selectedIndex = 0,
  setSelectedIndex,
  alt,
  ...modalProps
}: ImageModalProps) {
  const imageSource = useOptimizedImage(
    selectedIndex !== undefined ? sources[selectedIndex] : undefined,
    {}
  )

  const showPrevious = !!setSelectedIndex && selectedIndex !== 0
  const showNext = !!setSelectedIndex && selectedIndex !== sources.length - 1

  const handlePrevious = useCallback(
    () => setSelectedIndex && setSelectedIndex(selectedIndex - 1),
    [selectedIndex, setSelectedIndex]
  )

  const handleNext = useCallback(
    () => setSelectedIndex && setSelectedIndex(selectedIndex + 1),
    [selectedIndex, setSelectedIndex]
  )

  return (
    <Modal
      {...modalProps}
      fit
      inline
      className='!border-0 sm:rounded-md w-full h-dscreen sm:w-3/4 sm:h-3/4 !overflow-y-hidden'
    >
      <div
        className='absolute inset-0 bg-cover -z-[1] bg-center'
        style={{ backgroundImage: `url(${imageSource})` }}
      >
        <div className='absolute inset-0 bg-neutral-800 opacity-[.95]' />
      </div>
      <div className='flex items-center justify-center w-full h-full'>
        <img
          alt={alt}
          src={imageSource}
          className='w-auto max-h-full mx-auto my-auto'
        />
      </div>
      {showPrevious && (
        <div className='absolute -translate-y-1/2 left-2 top-1/2'>
          <Button.Secondary
            onClick={handlePrevious}
            className='rounded-full !p-2'
          >
            <ChevronLeftIcon className='w-5 h-5' />
          </Button.Secondary>
        </div>
      )}
      {showNext && (
        <div className='absolute -translate-y-1/2 right-2 top-1/2'>
          <Button.Secondary onClick={handleNext} className='rounded-full !p-2'>
            <ChevronRightIcon className='w-5 h-5' />
          </Button.Secondary>
        </div>
      )}
    </Modal>
  )
}
